import {
  ApiError,
  ApiNetworkError,
  type EnvironmentSummary,
  type FleetSummaryResponse,
  type TenantSummary,
  adminAuthLogout,
  fetchFleetSummary,
} from '@cortex/web-api/clients/admin/api';
import { Wordmark } from '@cortex/web-api/ui/admin/Wordmark';
import { useEffect, useState } from 'react';

/**
 * v1.2 admin fleet view — single screen showing today's stats for the
 * local tenant. Per-card error state (decision 3A); '—' for absent stats
 * (decision 6B); 401 from any /admin/* call → bubble up to App.tsx for
 * re-login (decision 7).
 *
 * For now there's just one tenant card (the demo tenant served by this
 * container). Multi-tenant aggregation lands when there's a 2nd tenant
 * deployed.
 */

interface Props {
  onSessionExpired: () => void;
  onLoggedOut: () => void;
  onSwitchToClients: () => void;
  onSwitchToExecutions: () => void;
}

export function FleetScreen({
  onSessionExpired,
  onLoggedOut,
  onSwitchToClients,
  onSwitchToExecutions,
}: Props) {
  const [data, setData] = useState<FleetSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchFleetSummary();
      setData(res);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSessionExpired();
        return;
      }
      if (err instanceof ApiNetworkError) {
        setError("Couldn't reach the server.");
      } else {
        setError('Could not load fleet stats.');
      }
    } finally {
      setLoading(false);
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load is stable in this scope; intentional one-shot on mount
  useEffect(() => {
    void load();
  }, []);

  async function handleSignOut() {
    await adminAuthLogout();
    onLoggedOut();
  }

  return (
    <div className="app">
      <header className="app-header" style={{ justifyContent: 'space-between', display: 'flex' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <img src="/acel-cortex-mark.svg" alt="" width={32} height={32} />
          <Wordmark />
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <button
            type="button"
            onClick={onSwitchToClients}
            className="btn-secondary"
            style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
          >
            clients
          </button>
          <button
            type="button"
            onClick={onSwitchToExecutions}
            className="btn-secondary"
            style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
          >
            executions
          </button>
          <button
            type="button"
            onClick={handleSignOut}
            className="btn-secondary"
            style={{ padding: '8px 16px', fontSize: 'var(--fs-small)' }}
          >
            sign out
          </button>
        </div>
      </header>
      <main
        className="app-main"
        style={{ overflow: 'auto', padding: 'var(--space-8) var(--space-6)' }}
      >
        <div
          style={{
            maxWidth: 960,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-6)',
          }}
        >
          <h1 className="h2" style={{ color: 'var(--ink)' }}>
            fleet
          </h1>

          {loading && data === null ? (
            <p className="field-help">loading…</p>
          ) : error && data === null ? (
            <div className="card" style={{ padding: 'var(--space-6)' }}>
              <p className="field-error">{error}</p>
              <button
                type="button"
                onClick={load}
                className="btn-secondary"
                style={{ marginTop: 'var(--space-4)' }}
              >
                retry
              </button>
            </div>
          ) : (
            <>
              {data?.tenants.map((t) => (
                <TenantCard key={t.tenant} tenant={t} onRefresh={load} loading={loading} />
              ))}
              {data?.environments?.map((env) => (
                <EnvironmentCard key={env.name} env={env} />
              ))}
            </>
          )}
        </div>
      </main>
      <footer className="app-footer">acel·cortex internal</footer>
    </div>
  );
}

interface CardProps {
  tenant: TenantSummary;
  onRefresh: () => void;
  loading: boolean;
}

function TenantCard({ tenant, onRefresh, loading }: CardProps) {
  const allZero =
    tenant.conversationsToday === 0 && tenant.tokens24h === 0 && tenant.lastInboundAt === null;

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-5)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <span
            aria-label="healthy"
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: 'var(--green)',
              display: 'inline-block',
            }}
          />
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
              letterSpacing: 'var(--tracking-mono)',
              textTransform: 'uppercase',
              color: 'var(--ink)',
            }}
          >
            {tenant.tenant}
          </span>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="btn-secondary"
          style={{ padding: '6px 12px', fontSize: 'var(--fs-small)' }}
        >
          {loading ? 'refreshing…' : 'refresh'}
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 'var(--space-4)',
        }}
      >
        <Stat
          label="conversations today"
          value={tenant.conversationsToday > 0 ? String(tenant.conversationsToday) : '—'}
        />
        <Stat label="last inbound" value={formatRelative(tenant.lastInboundAt)} />
        <Stat label="outbound (24h)" value={formatOutbound(tenant.outbound24h)} />
        <Stat
          label="tokens (24h)"
          value={tenant.tokens24h > 0 ? formatNumber(tenant.tokens24h) : '—'}
        />
      </div>

      <div
        style={{
          marginTop: 'var(--space-5)',
          paddingTop: 'var(--space-4)',
          borderTop: '1px solid var(--rule)',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-mono-sm)',
          letterSpacing: 'var(--tracking-mono)',
          textTransform: 'uppercase',
          color: 'var(--muted)',
        }}
      >
        open tasks (coming soon)
      </div>

      {allZero && (
        <p className="field-help" style={{ marginTop: 'var(--space-3)', fontStyle: 'italic' }}>
          no activity yet
        </p>
      )}
    </div>
  );
}

interface EnvironmentCardProps {
  env: EnvironmentSummary;
}

/**
 * Non-tenant environment card (sandbox lane). Same card grammar as
 * TenantCard: status dot + mono uppercase label header, then the stat grid
 * when the HMAC-gated stats probe succeeded. Build SHA (+ deployed branch
 * when the sandbox exposes it) rides in the footer rule, and the header
 * links out to the environment itself.
 */
function EnvironmentCard({ env }: EnvironmentCardProps) {
  const healthy = env.status === 'healthy';

  return (
    <div className="card" style={{ padding: 'var(--space-6)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-5)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          <span
            aria-label={healthy ? 'healthy' : 'unreachable'}
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: healthy ? 'var(--green)' : '#c84a3b',
              display: 'inline-block',
            }}
          />
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--fs-mono-sm)',
              letterSpacing: 'var(--tracking-mono)',
              textTransform: 'uppercase',
              color: 'var(--ink)',
            }}
          >
            {env.name} · test environment
          </span>
        </div>
        <a
          href={env.url}
          target="_blank"
          rel="noreferrer"
          className="btn-secondary"
          style={{ padding: '6px 12px', fontSize: 'var(--fs-small)', textDecoration: 'none' }}
        >
          open {env.name} →
        </a>
      </div>

      {env.stats ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 'var(--space-4)',
          }}
        >
          <Stat
            label="conversations today"
            value={env.stats.conversationsToday > 0 ? String(env.stats.conversationsToday) : '—'}
          />
          <Stat label="last inbound" value={formatRelative(env.stats.lastInboundAt)} />
          <Stat label="outbound (24h)" value={formatOutbound(env.stats.outbound24h)} />
          <Stat
            label="tokens (24h)"
            value={env.stats.tokens24h > 0 ? formatNumber(env.stats.tokens24h) : '—'}
          />
        </div>
      ) : (
        <p className="field-help" style={{ fontStyle: 'italic' }}>
          {healthy ? 'stats unavailable' : 'environment unreachable'}
        </p>
      )}

      <div
        style={{
          marginTop: 'var(--space-5)',
          paddingTop: 'var(--space-4)',
          borderTop: '1px solid var(--rule)',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-mono-sm)',
          letterSpacing: 'var(--tracking-mono)',
          textTransform: 'uppercase',
          color: 'var(--muted)',
        }}
      >
        build {env.buildSha ?? '—'}
        {env.deployedBranch ? ` · ${env.deployedBranch}` : ''}
      </div>
    </div>
  );
}

interface StatProps {
  label: string;
  value: string;
}

function Stat({ label, value }: StatProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-mono-sm)',
          letterSpacing: 'var(--tracking-mono)',
          textTransform: 'uppercase',
          color: 'var(--muted)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-h3)',
          fontWeight: 500,
          color: 'var(--ink)',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function formatOutbound(b: TenantSummary['outbound24h']): string {
  const total = b.sent + b.failed + b.not_attempted;
  if (total === 0) return '—';
  const parts = [`${b.sent} sent`];
  if (b.failed > 0) parts.push(`${b.failed} failed`);
  if (b.not_attempted > 0) parts.push(`${b.not_attempted} n/a`);
  return parts.join(' · ');
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-US').format(n);
}
